import { useAuthContext } from '@/auth/useAuthContext';
import { useToast } from '@/components/Toast';
import { ApiError, updateAccount } from '@/lib/api';
import { SPACE_NOUN_PRESETS, matchSpaceNounPreset, resolveSpaceNounForms } from '@/lib/spaceNoun';
import type { Account, UpdateAccountInput } from '@/lib/types';
import { useCallback, useEffect, useState } from 'react';
import { BookingRequestSettingsSection } from './BookingRequestSettingsSection';

// Mirrors the API validator (apps/api/src/accounts/space_noun.ts): letters,
// spaces and hyphens, 1–24 characters. Empty = platform default ("run").
const SPACE_NOUN_RE = /^[\p{L}][\p{L} -]{0,23}$/u;
const CUSTOM_PRESET = '__custom__';

const CURRENCIES = ['GBP', 'USD', 'EUR', 'CAD', 'AUD', 'NZD'] as const;
type Currency = (typeof CURRENCIES)[number];

function isCurrency(v: string): v is Currency {
  return (CURRENCIES as readonly string[]).includes(v);
}

interface FormState {
  name: string;
  currency: string;
  defaultTimezone: string;
  locale: string;
  publicPhone: string;
  // Migration 0097 — '' means "platform default" (stored as NULL).
  spaceNounSingular: string;
  spaceNounPlural: string;
}

function formFromAccount(account: Account): FormState {
  return {
    name: account.name,
    currency: account.currency,
    defaultTimezone: account.defaultTimezone,
    locale: account.locale,
    publicPhone: account.publicPhone ?? '',
    spaceNounSingular: account.spaceNounSingular ?? '',
    spaceNounPlural: account.spaceNounPlural ?? '',
  };
}

function isDirty(form: FormState, current: Account): boolean {
  const currentPhone = current.publicPhone ?? '';
  return (
    form.name !== current.name ||
    form.currency !== current.currency ||
    form.defaultTimezone !== current.defaultTimezone ||
    form.locale !== current.locale ||
    form.publicPhone !== currentPhone ||
    form.spaceNounSingular !== (current.spaceNounSingular ?? '') ||
    form.spaceNounPlural !== (current.spaceNounPlural ?? '')
  );
}

/** Which preset (or custom) the current singular/plural pair corresponds to. */
function presetValueFor(form: FormState): string {
  const resolved = resolveSpaceNounForms({
    singular: form.spaceNounSingular,
    plural: form.spaceNounPlural,
  });
  const preset = matchSpaceNounPreset(resolved);
  return preset ? preset.singular : CUSTOM_PRESET;
}

export function AccountSettings() {
  const { state, refresh } = useAuthContext();
  const account = state.me?.activeAccount ?? null;
  const { showToast, ToastContainer } = useToast();

  const [form, setForm] = useState<FormState>({
    name: '',
    currency: 'GBP',
    defaultTimezone: '',
    locale: '',
    publicPhone: '',
    spaceNounSingular: '',
    spaceNounPlural: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Sync form whenever the account data loads / refreshes
  useEffect(() => {
    if (account) {
      setForm(formFromAccount(account));
    }
  }, [account]);

  const handleReset = useCallback(() => {
    if (account) {
      setForm(formFromAccount(account));
      setError(null);
      setValidationErrors([]);
    }
  }, [account]);

  const validate = useCallback((): string[] => {
    const errs: string[] = [];
    if (form.name.trim().length < 1 || form.name.trim().length > 80) {
      errs.push('Business name must be between 1 and 80 characters.');
    }
    if (!isCurrency(form.currency)) {
      errs.push(`Currency must be one of: ${CURRENCIES.join(', ')}.`);
    }
    if (form.defaultTimezone.trim().length === 0) {
      errs.push('Timezone must not be empty.');
    }
    if (form.locale.trim().length === 0) {
      errs.push('Locale must not be empty.');
    }
    const singular = form.spaceNounSingular.trim();
    const plural = form.spaceNounPlural.trim();
    if (singular !== '' && !SPACE_NOUN_RE.test(singular)) {
      errs.push('Space name (singular) must be 1–24 letters, spaces or hyphens.');
    }
    if (plural !== '' && !SPACE_NOUN_RE.test(plural)) {
      errs.push('Space name (plural) must be 1–24 letters, spaces or hyphens.');
    }
    if (plural !== '' && singular === '') {
      errs.push('Set the singular space name as well as the plural.');
    }
    return errs;
  }, [form]);

  async function handleSave() {
    if (!account) return;
    const errs = validate();
    if (errs.length > 0) {
      setValidationErrors(errs);
      return;
    }
    setValidationErrors([]);

    const currentPhone = account.publicPhone ?? '';
    const patch: UpdateAccountInput = {
      ...(form.name !== account.name ? { name: form.name.trim() } : {}),
      ...(form.currency !== account.currency ? { currency: form.currency } : {}),
      ...(form.defaultTimezone !== account.defaultTimezone
        ? { defaultTimezone: form.defaultTimezone.trim() }
        : {}),
      ...(form.locale !== account.locale ? { locale: form.locale.trim() } : {}),
      ...(form.publicPhone !== currentPhone
        ? { publicPhone: form.publicPhone.trim() || null }
        : {}),
      ...(form.spaceNounSingular !== (account.spaceNounSingular ?? '')
        ? { spaceNounSingular: form.spaceNounSingular.trim() || null }
        : {}),
      ...(form.spaceNounPlural !== (account.spaceNounPlural ?? '')
        ? { spaceNounPlural: form.spaceNounPlural.trim() || null }
        : {}),
    };

    if (Object.keys(patch).length === 0) return;

    setSaving(true);
    setError(null);

    try {
      await updateAccount(patch);
      await refresh();
      showToast('Account updated successfully.', 'success');
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  if (state.loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
        <div className="h-4 w-full animate-pulse rounded bg-secondary" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-secondary" />
      </div>
    );
  }

  if (!account) {
    return <p className="text-sm text-muted-foreground">No active account.</p>;
  }

  const dirty = isDirty(form, account);
  const presetValue = presetValueFor(form);
  const previewNoun = resolveSpaceNounForms({
    singular: form.spaceNounSingular,
    plural: form.spaceNounPlural,
  });

  function applyPreset(value: string) {
    if (value === CUSTOM_PRESET) {
      // Keep whatever is typed; just switch the inputs into "custom" mode.
      setForm((f) => ({
        ...f,
        spaceNounSingular: f.spaceNounSingular || previewNoun.singular,
        spaceNounPlural: f.spaceNounPlural || previewNoun.plural,
      }));
      return;
    }
    const preset = SPACE_NOUN_PRESETS.find((p) => p.singular === value);
    if (!preset) return;
    // "Run" is the platform default — store empty so the columns go back to NULL.
    const isDefault = preset.singular === 'run';
    setForm((f) => ({
      ...f,
      spaceNounSingular: isDefault ? '' : preset.singular,
      spaceNounPlural: isDefault ? '' : preset.plural,
    }));
  }

  return (
    <div className="space-y-8">
      <ToastContainer />
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Account</h1>
      </header>

      <section className="rounded-2xl bg-surface ring-1 ring-border elevate">
        <div className="divide-y divide-border">
          {/* Business name */}
          <div className="px-6 py-5">
            <label
              htmlFor="account-name"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Business name
            </label>
            <input
              id="account-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={80}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Your kennel's name"
            />
          </div>

          {/* Currency */}
          <div className="px-6 py-5">
            <label
              htmlFor="account-currency"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Currency
            </label>
            <select
              id="account-currency"
              value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Timezone */}
          <div className="px-6 py-5">
            <label
              htmlFor="account-timezone"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Default timezone
            </label>
            <input
              id="account-timezone"
              type="text"
              value={form.defaultTimezone}
              onChange={(e) => setForm((f) => ({ ...f, defaultTimezone: e.target.value }))}
              placeholder="Europe/London"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <p className="mt-1 text-xs text-muted-foreground">IANA timezone identifier</p>
          </div>

          {/* Locale */}
          <div className="px-6 py-5">
            <label
              htmlFor="account-locale"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Locale
            </label>
            <input
              id="account-locale"
              type="text"
              value={form.locale}
              onChange={(e) => setForm((f) => ({ ...f, locale: e.target.value }))}
              placeholder="en-GB"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <p className="mt-1 text-xs text-muted-foreground">BCP-47 locale tag</p>
          </div>

          {/* Public phone */}
          <div className="px-6 py-5">
            <label
              htmlFor="account-public-phone"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Public phone number
            </label>
            <input
              id="account-public-phone"
              type="tel"
              value={form.publicPhone}
              onChange={(e) => setForm((f) => ({ ...f, publicPhone: e.target.value }))}
              placeholder="+44 1234 000000"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Shown on your customer booking portal landing page
            </p>
          </div>

          {/* Space noun — migration 0097 */}
          <div className="px-6 py-5">
            <label
              htmlFor="account-space-noun-preset"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              What do you call a pet space?
            </label>
            <select
              id="account-space-noun-preset"
              value={presetValue}
              onChange={(e) => applyPreset(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {SPACE_NOUN_PRESETS.map((p) => (
                <option key={p.singular} value={p.singular}>
                  {p.label}
                  {p.singular === 'run' ? ' (default)' : ''}
                </option>
              ))}
              <option value={CUSTOM_PRESET}>Custom…</option>
            </select>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="account-space-noun-singular"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Singular
                </label>
                <input
                  id="account-space-noun-singular"
                  type="text"
                  value={form.spaceNounSingular}
                  onChange={(e) => setForm((f) => ({ ...f, spaceNounSingular: e.target.value }))}
                  placeholder="run"
                  maxLength={24}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <div>
                <label
                  htmlFor="account-space-noun-plural"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Plural
                </label>
                <input
                  id="account-space-noun-plural"
                  type="text"
                  value={form.spaceNounPlural}
                  onChange={(e) => setForm((f) => ({ ...f, spaceNounPlural: e.target.value }))}
                  placeholder={form.spaceNounSingular ? `${form.spaceNounSingular}s` : 'runs'}
                  maxLength={24}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Used everywhere a space is named — staff app, customer portal and printed cards.
              Preview: &ldquo;Assign a {previewNoun.singular}&rdquo; · &ldquo;
              {previewNoun.Plural}&rdquo;. Leave blank for the default (run / runs).
            </p>
          </div>

          {/* Slug (read-only) */}
          <div className="px-6 py-5">
            <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Slug — used in URLs and exports
            </p>
            <p className="num select-all rounded-md border border-border bg-secondary px-3 py-2 text-sm text-muted-foreground">
              {account.slug}
            </p>
          </div>
        </div>

        {/* Feedback + actions */}
        <div className="border-t border-border px-6 py-5">
          {validationErrors.length > 0 && (
            <ul className="mb-4 space-y-1">
              {validationErrors.map((e) => (
                <li key={e} className="text-sm text-destructive">
                  {e}
                </li>
              ))}
            </ul>
          )}
          {error !== null && <p className="mb-4 text-sm text-destructive">{error}</p>}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!dirty || saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={!dirty || saving}
              className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground press disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      </section>

      {/* Migration 0095 — request-and-approve booking mode (account-wide rules) */}
      <BookingRequestSettingsSection />
    </div>
  );
}
